import type { Identifier } from "ra-core";

import { buildUnclassifiedRows } from "../../whatsapp/unclassifiedRules";
import type {
  SetCanClassifyWhatsAppContactsParams,
  WhatsAppClassifyLeadParams,
  WhatsAppClassifyLeadResult,
  WhatsAppMarkPersonalResult,
  WhatsAppPersonalLoadResult,
  WhatsAppPersonalNumberRow,
  WhatsAppPersonalNumbersPage,
  WhatsAppUnclassifiedMessageRow,
  WhatsAppUnclassifiedPage,
} from "../../types";
import { getSupabaseClient } from "./supabase";
import { fail, withOwnReadMarks, type ConversationBase } from "./whatsappInbox";

/**
 * Almikia Epic EW3 (TASK-095): Supabase side of the 'Sin clasificar' tray and the personal-numbers
 * list. Reads go through the RLS-bound table / view of TASK-093 (anyone without the permission
 * reads zero rows) and every write is a SECURITY DEFINER RPC that checks the permission itself,
 * so the UI is never the only gate. Only the tray (permission holder) reads the sender number and
 * profile name; the inbox rows and every other screen never do.
 */
const CONVERSATION_COLUMNS =
  "id, deal_id, last_message_at, last_inbound_at, last_outbound_at, phone, profile_name, contact_id";
const MESSAGES_PER_ROW = 20;
type TrayConversationBase = ConversationBase & {
  phone: string | null;
  profile_name: string | null;
  contact_id: Identifier | null;
};

export const createSupabaseWhatsAppUnclassifiedMethods = () => ({
  /** The newest `limit` unclassified conversations (the screen grows the limit by 100). */
  async getWhatsAppUnclassified(params: {
    limit: number;
  }): Promise<WhatsAppUnclassifiedPage> {
    const client = getSupabaseClient();
    const { data, error, count } = await client
      .from("whatsapp_conversations")
      .select(CONVERSATION_COLUMNS, { count: "exact" })
      .is("deal_id", null)
      .order("last_message_at", { ascending: false, nullsFirst: false })
      .limit(params.limit);
    fail(error, "No se pudo leer la bandeja de Sin clasificar");
    const conversations = await withOwnReadMarks(
      (data ?? []) as TrayConversationBase[],
    );
    if (conversations.length === 0) return { rows: [], total: count ?? 0 };
    const { data: messages, error: messagesError } = await client
      .from("whatsapp_messages")
      .select("conversation_id, direction, type, body, message_at, origin")
      .in(
        "conversation_id",
        conversations.map((row) => row.id),
      )
      .order("message_at", { ascending: false })
      .limit(conversations.length * MESSAGES_PER_ROW);
    fail(messagesError, "No se pudieron leer los mensajes de WhatsApp");
    // Known contacts: read the names of the bound contacts (RLS may hide one: the row then shows
    // the link without a name).
    const contactIds = [
      ...new Set(
        conversations
          .map((row) => row.contact_id)
          .filter((id): id is Identifier => id != null),
      ),
    ];
    const contactNames = new Map<string, string>();
    if (contactIds.length > 0) {
      const { data: contacts } = await client
        .from("contacts")
        .select("id, first_name, last_name")
        .in("id", contactIds);
      for (const contact of (contacts ?? []) as {
        id: Identifier;
        first_name: string | null;
        last_name: string | null;
      }[]) {
        const name =
          `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim();
        if (name) contactNames.set(String(contact.id), name);
      }
    }
    return {
      rows: buildUnclassifiedRows(
        conversations,
        (messages ?? []) as WhatsAppUnclassifiedMessageRow[],
        contactNames,
      ),
      total: count ?? conversations.length,
    };
  },

  /** 'Es lead': creates the lead from the conversation (the RPC checks permission and rules). */
  async classifyWhatsAppLead(
    params: WhatsAppClassifyLeadParams,
  ): Promise<WhatsAppClassifyLeadResult> {
    const { data, error } = await getSupabaseClient().rpc(
      "classify_whatsapp_lead",
      {
        p_conversation_id: params.conversationId,
        p_project_id: params.projectId,
        p_lead_type: params.leadType,
        p_lead_source_id: params.leadSourceId,
        p_assign_mode: params.assignMode,
        p_sales_id: params.assignMode === "advisor" ? params.salesId : null,
        p_business_unit: params.businessUnit ?? "bpo",
      },
    );
    fail(error, "No se pudo clasificar la conversacion como lead");
    const result = data as {
      conversation_id: Identifier;
      deal_id: Identifier;
      contact_id: Identifier;
      sales_id: Identifier | null;
    };
    return {
      conversationId: result.conversation_id,
      dealId: result.deal_id,
      contactId: result.contact_id,
      salesId: result.sales_id ?? null,
    };
  },

  /** 'Personal': hard-deletes the stored messages and conversation and lists the number. */
  async markWhatsAppNumberPersonal(
    conversationId: Identifier,
  ): Promise<WhatsAppMarkPersonalResult> {
    const { data, error } = await getSupabaseClient().rpc(
      "mark_whatsapp_number_personal",
      { p_conversation_id: conversationId },
    );
    fail(error, "No se pudo marcar el numero como personal");
    const result = data as {
      phone_last4: string;
      conversations_deleted: number;
    };
    return {
      phoneLast4: result.phone_last4,
      conversationsDeleted: result.conversations_deleted,
    };
  },

  /** Bulk load; the server canonicalizes, de-duplicates and refuses numbers of leads. */
  async loadWhatsAppPersonalNumbers(
    numbers: string[],
  ): Promise<WhatsAppPersonalLoadResult> {
    const { data, error } = await getSupabaseClient().rpc(
      "load_whatsapp_personal_numbers",
      { p_numbers: numbers },
    );
    fail(error, "No se pudo cargar la lista de numeros personales");
    return data as WhatsAppPersonalLoadResult;
  },

  /** One page of the personal list (newest first). */
  async listWhatsAppPersonalNumbers(params: {
    page: number;
    perPage: number;
  }): Promise<WhatsAppPersonalNumbersPage> {
    const from = (params.page - 1) * params.perPage;
    const { data, error, count } = await getSupabaseClient()
      .from("whatsapp_personal_numbers_view")
      .select(
        "phone, phone_last4, dropped_count, last_seen_at, source, added_at",
        {
          count: "exact",
        },
      )
      .order("added_at", { ascending: false })
      .range(from, from + params.perPage - 1);
    fail(error, "No se pudo leer la lista de numeros personales");
    const rows = (
      (data ?? []) as {
        phone: string;
        phone_last4: string;
        dropped_count: number;
        last_seen_at: string | null;
        source: WhatsAppPersonalNumberRow["source"];
        added_at: string;
      }[]
    ).map((row) => ({
      phone: row.phone,
      phoneLast4: row.phone_last4,
      droppedCount: row.dropped_count,
      lastSeenAt: row.last_seen_at,
      source: row.source,
      addedAt: row.added_at,
    }));
    return { rows, total: count ?? rows.length };
  },

  /** 'Quitar de la lista': messages already deleted are not restored. */
  async removeWhatsAppPersonalNumber(phone: string): Promise<boolean> {
    const { data, error } = await getSupabaseClient().rpc(
      "remove_whatsapp_personal_number",
      { p_phone: phone },
    );
    fail(error, "No se pudo quitar el numero de la lista");
    return data === true;
  },

  /** Direccion-only switch for `sales.can_classify_whatsapp_contacts` (never on her own row). */
  async setCanClassifyWhatsAppContacts({
    salesId,
    value,
  }: SetCanClassifyWhatsAppContactsParams): Promise<boolean> {
    const { data, error } = await getSupabaseClient().rpc(
      "set_can_classify_whatsapp_contacts",
      { p_sales_id: salesId, p_value: value },
    );
    if (error || typeof data !== "boolean") {
      throw new Error(error?.message || "No se pudo cambiar el permiso");
    }
    return data;
  },
});
